import type { CaseStudy } from '@portfolio/shared';

/**
 * Case studies are written strictly from approved CV content.
 *
 * Rules applied here:
 * - no client names, user counts, revenue, latency or percentage improvements;
 * - personal ownership is separated from team effort;
 * - no compliance or certification claims for healthcare or public sector work.
 */
export const caseStudies: readonly CaseStudy[] = [
  {
    slug: 'ibm-ai-code-transformation-platform',
    title: 'Enterprise AI Code Transformation Platform',
    organization: 'IBM',
    role: 'Senior Full Stack Developer, frontend-focused',
    period: { start: '2025-05', end: '2026-07', label: 'May 2025 - July 2026' },
    domain: 'Enterprise AI',
    cardSummary:
      'Frontend for a platform that migrates applications between programming languages using ' +
      'AI-assisted transformation, with configuration, execution and review workflows.',
    summary:
      'An enterprise platform that migrates applications between programming languages while ' +
      'preserving business logic. I worked on the frontend: the interfaces engineers use to ' +
      'configure a migration, run it, watch it progress, and review what the transformation ' +
      'produced.',
    focus: [
      'AI-assisted migration workflows',
      'Asynchronous job monitoring',
      'Generated-code review',
      'Accessible component library',
    ],
    ownership: [
      'Workflows for configuring, executing and monitoring AI-assisted code transformations.',
      'Source-code ingestion, project configuration and migration job creation interfaces.',
      'Transformation review, validation results and generated-code analysis interfaces.',
      'Dashboards presenting token usage, confidence scores and migration progress.',
      'Reusable accessible UI components, plus contributions to frontend architecture and coding standards.',
    ],
    sections: [
      {
        id: 'context',
        heading: 'Context',
        paragraphs: [
          'The product converts existing applications from one programming language to another ' +
            'without changing what the software does. A large language model performs the ' +
            'transformation, and a Python/FastAPI orchestration layer handles code parsing, LLM ' +
            'execution, validation and reporting.',
          'The frontend is where that pipeline becomes usable: it is how a migration is set up, ' +
            'started, observed while it runs, and inspected once it produces output.',
        ],
        bullets: [],
      },
      {
        id: 'problem',
        heading: 'What made the frontend hard',
        paragraphs: [
          'A code migration is not a request/response interaction. It is a long-running job with ' +
            'partial results, retries, and outcomes that can be valid, invalid, or somewhere in ' +
            'between. The interface has to make an inherently uncertain process legible.',
        ],
        bullets: [
          'Long-running jobs whose progress, errors and retries all have to be visible.',
          'Validation failures that are a normal outcome rather than an exception.',
          'Machine-generated output that engineers need to review before trusting it.',
          'Execution metadata arriving from several different backend systems.',
        ],
      },
      {
        id: 'responsibilities',
        heading: 'What I owned',
        paragraphs: [],
        bullets: [
          'Designed and implemented the configure / execute / monitor workflow for AI-assisted transformations.',
          'Built the source-code ingestion, project configuration and migration job creation screens.',
          'Built transformation review, validation results and generated-code analysis interfaces.',
          'Implemented dashboards for token usage, confidence scores and migration progress.',
          'Optimized rendering, state management, data fetching, frontend error handling and keyboard navigation.',
        ],
      },
      {
        id: 'architecture',
        heading: 'Frontend architecture',
        paragraphs: [
          'The application is built with React, Next.js and TypeScript. I contributed to the ' +
            'frontend architecture, coding standards and component design, and built reusable ' +
            'accessible UI components that the migration workflows are assembled from.',
        ],
        bullets: [
          'Typed component boundaries between configuration, execution and review surfaces.',
          'Shared accessible primitives rather than per-screen one-off components.',
          'State and data-fetching strategy tuned for screens that update while a job runs.',
        ],
      },
      {
        id: 'async',
        heading: 'Asynchronous workflow design',
        paragraphs: [
          'Migration jobs run for a long time and fail in more than one way. The interface models ' +
            'progress, errors, retries and validation failures as first-class states rather than ' +
            'treating anything other than success as an error case.',
        ],
        bullets: [
          'Explicit progress reporting while a transformation executes.',
          'Retry and error paths surfaced in the interface instead of hidden in logs.',
          'Validation failures presented as reviewable results.',
        ],
      },
      {
        id: 'quality',
        heading: 'Accessibility and performance',
        paragraphs: [
          'Accessibility work centred on reusable components and keyboard operation, so that the ' +
            'behaviour was consistent everywhere the components were used rather than fixed ' +
            'screen by screen.',
        ],
        bullets: [
          'Reusable accessible UI components shared across the platform.',
          'Keyboard navigation treated as part of each workflow, not an afterthought.',
          'Rendering and data-fetching optimization for dashboards that update continuously.',
        ],
      },
      {
        id: 'integration',
        heading: 'Integration architecture',
        paragraphs: [
          'The React/Next.js interfaces integrate with Python/FastAPI orchestration services over ' +
            'REST for code parsing, LLM execution, validation and reporting. Execution metadata ' +
            'from MCP, Snowflake and Databricks pipelines is surfaced in the interface.',
        ],
        bullets: [],
      },
    ],
    stack: [
      'React',
      'Next.js',
      'TypeScript',
      'JavaScript',
      'REST APIs',
      'Python',
      'FastAPI',
      'LLM workflows',
      'MCP servers',
      'Snowflake',
      'Databricks',
      'PostgreSQL',
    ],
    delivered: [
      'End-to-end migration workflow: ingestion, configuration, job creation, execution, review.',
      'Dashboards for token usage, confidence scores and migration progress.',
      'A set of reusable accessible UI components used across the platform.',
    ],
    relatedRoleId: 'ibm',
  },
  {
    slug: 'nationwide-disaster-alert-platform',
    title: 'Nationwide Disaster Alert Platform',
    organization: 'AROBS Transilvania Software',
    role: 'Senior Frontend Developer',
    period: { start: '2022-12', end: '2025-05', label: 'December 2022 - May 2025' },
    market: 'USA',
    domain: 'Public safety',
    cardSummary:
      'Mission-critical React and Vue.js interfaces for a nationwide emergency alert platform ' +
      'rendering high-volume real-time event streams.',
    summary:
      'A nationwide emergency alert platform for the United States market. I led React and ' +
      'Vue.js interface development, with the central engineering problem being how to render ' +
      'high-volume real-time event streams without the interface losing consistency or ' +
      'responsiveness.',
    focus: [
      'Real-time event streams',
      'RxJS backpressure',
      'TypeScript component library',
      'Data-heavy dashboards',
    ],
    ownership: [
      'Led React and Vue.js interface development for the alert platform.',
      'Architected reusable TypeScript component libraries for alert-management workflows.',
      'Implemented stream rendering with RxJS for backpressure and state consistency.',
      'Optimized data-heavy dashboards with code splitting, lazy loading and bundle optimization.',
    ],
    sections: [
      {
        id: 'context',
        heading: 'Context',
        paragraphs: [
          'The platform manages emergency alerts at national scale. Its interfaces are operated ' +
            'by people managing alert workflows, and the data behind those interfaces arrives ' +
            'continuously rather than on request.',
        ],
        bullets: [],
      },
      {
        id: 'problem',
        heading: 'What made the frontend hard',
        paragraphs: [
          'Two constraints shaped everything. First, events arrive faster than a browser can ' +
            'usefully repaint, so the interface needs a deliberate strategy for absorbing them. ' +
            'Second, this is software people rely on during emergencies, so state shown on screen ' +
            'has to stay consistent with the stream that produced it.',
        ],
        bullets: [
          'High-volume real-time event streams feeding live views.',
          'Two frontend frameworks (React and Vue.js) in the same product surface.',
          'Data-heavy dashboards that had to stay responsive.',
        ],
      },
      {
        id: 'streams',
        heading: 'Real-time stream handling',
        paragraphs: [
          'Event streams were rendered using RxJS, applying backpressure so that the rate of ' +
            'incoming events did not dictate the rate of rendering, and keeping displayed state ' +
            'consistent with the stream.',
        ],
        bullets: [
          'RxJS pipelines for backpressure and state consistency.',
          'Integration with Kafka and RabbitMQ event-driven systems through Node.js/NestJS services.',
        ],
      },
      {
        id: 'components',
        heading: 'Component architecture',
        paragraphs: [
          'Alert-management workflows repeat across the product, so I architected reusable ' +
            'TypeScript component libraries for them rather than rebuilding the same interactions ' +
            'per screen.',
        ],
        bullets: [
          'Typed, reusable components for alert-management workflows.',
          'Shared patterns across the React and Vue.js surfaces.',
        ],
      },
      {
        id: 'performance',
        heading: 'Performance',
        paragraphs: [
          'Dashboards carried a lot of data and a lot of code. Optimization focused on shipping ' +
            'less JavaScript up front and deferring what was not needed for the first meaningful ' +
            'view.',
        ],
        bullets: ['Code splitting.', 'Lazy loading.', 'Bundle optimization.'],
      },
    ],
    stack: [
      'React',
      'Vue.js',
      'TypeScript',
      'RxJS',
      'REST APIs',
      'Node.js',
      'NestJS',
      'Kafka',
      'RabbitMQ',
    ],
    delivered: [
      'Reusable TypeScript component libraries for alert-management workflows.',
      'Real-time stream rendering with RxJS backpressure and consistent state.',
      'Optimized data-heavy dashboards.',
    ],
    relatedRoleId: 'arobs',
  },
  {
    slug: 'telehealth-platform',
    title: 'Telehealth Product',
    organization: 'AROBS Transilvania Software',
    role: 'Senior Frontend Developer',
    period: { start: '2020-03', end: '2022-01', label: 'March 2020 - January 2022' },
    market: 'Israel',
    domain: 'Healthcare',
    cardSummary:
      'Next.js application for appointment scheduling and video consultations, with a shared ' +
      'Storybook component library for patient and provider modules.',
    summary:
      'An online healthcare platform for the Israeli market. I led development of the Next.js ' +
      'application covering appointment scheduling and video consultations, spanning both ' +
      'patient-facing and provider-facing modules.',
    focus: [
      'Next.js and server-side rendering',
      'Video consultation workflows',
      'Storybook component library',
      'Azure and Docker delivery',
    ],
    ownership: [
      'Led development of the Next.js appointment scheduling and video consultation application.',
      'Applied server-side rendering to improve initial-load performance.',
      'Established a Storybook component library for patient-facing and provider-facing modules.',
      'Architected secure patient-facing workflows for personal health data.',
    ],
    sections: [
      {
        id: 'context',
        heading: 'Context',
        paragraphs: [
          'The product connects patients with providers for scheduled appointments and video ' +
            'consultations. Two distinct audiences use it, with different needs but overlapping ' +
            'interface building blocks.',
        ],
        bullets: [],
      },
      {
        id: 'problem',
        heading: 'What made the frontend hard',
        paragraphs: [
          'Healthcare interfaces carry personal health data, which raises the cost of a sloppy ' +
            'workflow. At the same time the first load matters: someone joining a consultation ' +
            'should not be waiting on a JavaScript bundle.',
        ],
        bullets: [
          'Two audiences, patient-facing and provider-facing, sharing components.',
          'Personal health data in the workflows.',
          'Initial-load performance on scheduling and consultation entry points.',
        ],
      },
      {
        id: 'architecture',
        heading: 'Architecture and rendering',
        paragraphs: [
          'Server-side rendering was used to improve initial-load performance. A Storybook ' +
            'component library gave the patient-facing and provider-facing modules a shared, ' +
            'documented set of components rather than two diverging implementations.',
        ],
        bullets: [
          'Next.js with server-side rendering.',
          'Storybook component library shared across modules.',
          'Secure patient-facing workflows for personal health data.',
        ],
      },
      {
        id: 'delivery',
        heading: 'Delivery',
        paragraphs: ['The application was deployed on Azure using Docker.'],
        bullets: [],
      },
    ],
    stack: ['Next.js', 'React', 'TypeScript', 'Storybook', 'Node.js', 'Azure', 'Docker'],
    delivered: [
      'Appointment scheduling and video consultation application.',
      'Server-side rendered entry points.',
      'Storybook component library covering patient-facing and provider-facing modules.',
    ],
    relatedRoleId: 'arobs',
  },
  {
    slug: 'booking-management-saas',
    title: 'Multi-tenant Booking SaaS',
    organization: 'AROBS Transilvania Software',
    role: 'Senior Frontend Developer',
    period: { start: '2019-03', end: '2020-03', label: 'March 2019 - March 2020' },
    market: 'Germany and Switzerland',
    domain: 'SaaS',
    cardSummary:
      'React and Next.js frontend for a multi-tenant booking and appointment platform, with ' +
      'Apollo Client GraphQL caching and a shared Storybook library.',
    summary:
      'A multi-tenant booking and appointment platform used by barbershops and restaurants in ' +
      'Germany and Switzerland. I spearheaded the React and Next.js frontend.',
    focus: [
      'Multi-tenant frontend',
      'GraphQL with Apollo Client',
      'Shared Storybook components',
      'Booking and appointment workflows',
    ],
    ownership: [
      'Spearheaded the React and Next.js frontend of the booking and appointment platform.',
      'Introduced Apollo Client for GraphQL data fetching and caching.',
      'Maintained a shared Storybook component library.',
    ],
    sections: [
      {
        id: 'context',
        heading: 'Context',
        paragraphs: [
          'One platform serving multiple tenants across two markets, covering booking and ' +
            'appointment management for hospitality businesses with quite different scheduling ' +
            'shapes: barbershops and restaurants.',
        ],
        bullets: [],
      },
      {
        id: 'problem',
        heading: 'What made the frontend hard',
        paragraphs: [
          'Multi-tenancy pushes variation into the frontend. The same screens serve different ' +
            'business types, so the component layer has to absorb that variation without ' +
            'forking per tenant.',
        ],
        bullets: [
          'Tenant variation across two business types and two markets.',
          'Booking data that has to stay coherent while it is edited and re-fetched.',
        ],
      },
      {
        id: 'data',
        heading: 'Data layer',
        paragraphs: [
          'Apollo Client was introduced for GraphQL data fetching and caching, giving the ' +
            'booking views a shared cache rather than per-screen fetch logic.',
        ],
        bullets: [],
      },
      {
        id: 'components',
        heading: 'Component library',
        paragraphs: [
          'A shared Storybook component library kept the tenant-facing surfaces consistent and ' +
            'gave the team a place to review components in isolation.',
        ],
        bullets: [],
      },
    ],
    stack: ['React', 'Next.js', 'TypeScript', 'Apollo Client', 'GraphQL', 'Storybook', 'Node.js'],
    delivered: [
      'React and Next.js frontend for the multi-tenant booking platform.',
      'GraphQL data fetching and caching with Apollo Client.',
      'Shared Storybook component library.',
    ],
    relatedRoleId: 'arobs',
  },
];

export function findCaseStudy(slug: string | undefined): CaseStudy | undefined {
  return caseStudies.find((caseStudy) => caseStudy.slug === slug);
}
